import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../lib/i18n'; // Import the configured i18n instance

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Function to detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  return browserLang.startsWith('ar') ? 'ar' : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // First check if user has manually selected a language
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      return savedLanguage;
    }
    
    // If no manual selection, detect browser language
    return detectBrowserLanguage();
  });

  const [isRTL, setIsRTL] = useState(language === 'ar');

  useEffect(() => {
    localStorage.setItem('selectedLanguage', language);
    setIsRTL(language === 'ar');
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Sync with i18next
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  // Wrapper for translation function to maintain compatibility
  const t = (key) => {
    return i18n.t(key);
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const dir = isRTL ? 'rtl' : 'ltr';
  const value = {
    language,
    setLanguage,
    toggleLanguage,
    isRTL,
    dir,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
